import { FC, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useCurrentUser from '@/api/useCurrentUser.ts';
import useAllUserPaperAttempts from '@/api/useAllUserPaperAttempts';
import usePastPapers from '@/api/usePastPapers.ts';
import Routes from '@/router/Routes.ts';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import CompletionChart from '@/components/ui/StatCard/CompletionChart';
import AverageGradeChart from '@/components/ui/StatCard/AverageGradeChart';
import useProgressStats from '@/hooks/useProgressStats';

const HomePage: FC = () => {
  const navigate = useNavigate();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { data: attempts = [] } = useAllUserPaperAttempts(currentUser?.auth_id);
  const { data: pastPapers = [] } = usePastPapers();

  // Redirect to onboarding if tutorial not seen or onboarding not completed
  useEffect(() => {
    if (!isLoadingUser && currentUser) {
      if (!currentUser.tutorial_seen) {
        navigate(Routes.ONBOARDING);
      } else if (!currentUser.onboarding_completed) {
        navigate(Routes.STUDENT_ONBOARDING);
      }
    }
  }, [currentUser, isLoadingUser, navigate]);

  // Calculate stats using the custom hook
  const stats = useProgressStats(attempts, pastPapers.length);

  const dashboardCards = [
    {
      title: 'Practice Area',
      description: 'Practice each question type, one at a time, hints, model answers, help sheets and feedback given',
      buttonText: "Let's start",
      route: Routes.PRACTICE_AREA,
    },
    {
      title: 'Sit a Full Past Paper',
      description: 'One sitting, exam conditions and no hints given. Feedback and grade given after completion',
      buttonText: "Let's start",
      route: Routes.FULL_PAST_PAPERS,
    },
    {
      title: 'Progress Tracker',
      description: 'Track your progress on practice questions, mock papers and full past papers',
      buttonText: 'Read more',
      route: Routes.PROGRESS_TRACKER,
    },
    {
      title: 'Download Past Papers',
      description: 'Download a past paper to view or print',
      buttonText: 'Read more',
      route: Routes.DOWNLOAD_PAST_PAPERS,
    },
  ];

  return (
    <>
      <PageHeader title="Dashboard" />
      <div className="flex flex-col gap-4">
        {/* Stats Cards */}
        <div className="flex flex-wrap justify-center gap-6">
          <CompletionChart completedCount={stats.completedPapers} totalCount={stats.totalPapers} size="small" />
          <AverageGradeChart avgGrade={stats.avgGrade} gradeImprovement={stats.gradeImprovement} size="small" />
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dashboardCards.map((card, index) => (
            <Link
              key={index}
              to={card.route}
              className={`bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 p-8 hover:shadow-lg transition-shadow flex flex-col justify-between h-[300px] w-full max-w-[480px]  ${index % 2 === 0 ? 'justify-self-end' : 'justify-self-start'}`}
            >
              <div className="flex flex-col gap-4">
                <h3 className="text-3xl font-bold leading-tight">{card.title}</h3>
                <p className="text-gray-600 text-base">{card.description}</p>
              </div>
              <button className="bg-primary text-white py-4 px-6 rounded-xl hover:bg-dark-hover-secondary transition-colors w-full font-medium">
                {card.buttonText}
              </button>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

export default HomePage;
